import {
  hasRole,
  MAX_THREAD_RUNS,
  type AgentDto,
  type ChatMessage,
  type RunDetailDto,
  type RunDto,
  type RunSource,
  type RunStatus,
  type ScoreDto,
  type ScorerDto,
  type ThreadCaller,
  type ThreadDetailDto,
  type ThreadDto,
  type ThreadRunDto,
  type WorkflowRunDto,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  Bot,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Clock,
  Cpu,
  KeyRound,
  Layers,
  ListTree,
  MessagesSquare,
  Radio,
  Timer,
  User,
  Wrench,
  Workflow,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { MessageAttachments } from '@/components/attachments';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { Markdown } from '@/components/markdown';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import {
  CumulativeChart,
  EventTimeline,
  FlowTimeline,
  Facts,
  JsonBlock,
  PanelSection,
  RequestMessages,
  RoleTag,
  SessionChip,
  SessionMetric,
  SessionTitle,
  StatusPill,
  TokenTag,
  TranscriptItem,
  TranscriptScroll,
  TranscriptSearch,
  type TimelineEvent,
  type TimelineKind,
} from '@/components/session-view';
import { TraceWaterfall, type Span } from '@/components/trace-waterfall';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, errorMessage } from '@/lib/api';
import {
  formatCompact,
  COST_ESTIMATE_NOTE,
  costCell,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelative,
  formatTime,
  formatHitRate,
  formatUsd,
  RUN_SOURCE_LABELS,
  RUN_STATUS_LABELS,
} from '@/lib/format';
import { useMe, usePaged } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { WORKFLOW_STATUS_LABEL, WORKFLOW_STATUS_VARIANT } from '@/lib/workflow-ui';
import { ScoreBar } from './scorers';

const ms = (iso: string) => new Date(iso).getTime();
const ALL = 'all';
const STATUSES: RunStatus[] = ['running', 'succeeded', 'failed', 'cancelled', 'timeout'];
const RANGES = { all: 'Any time', '1h': 'Last hour', '24h': 'Last 24 hours', '7d': 'Last 7 days' };
const RANGE_MS = { '1h': 3_600_000, '24h': 86_400_000, '7d': 604_800_000 } as const;
const SOURCES: Array<{ value: RunSource; label: string }> = [
  { value: 'gateway', label: 'Endpoint (API)' },
  { value: 'playground', label: 'Playground' },
  { value: 'workflow', label: 'Workflow' },
];

export const TRACE_TABS = ['runs', 'threads', 'workflows'] as const;
export type TraceTab = (typeof TRACE_TABS)[number];

/** Search params of the thread page: a thread is an agent, a thread id and a caller. */
export interface ThreadSearch {
  agentId: string;
  threadId: string;
  apiKeyId?: string;
  userId?: string;
}

export function threadSearch(run: {
  agentId: string;
  threadId: string;
  apiKeyId: string | null;
  userId: string | null;
}): ThreadSearch {
  return {
    agentId: run.agentId,
    threadId: run.threadId,
    ...(run.apiKeyId ? { apiKeyId: run.apiKeyId } : {}),
    ...(run.userId ? { userId: run.userId } : {}),
  };
}

/** A run's status as a quiet dot + label: green done, pulsing blue running, red otherwise. */
export function RunStatusBadge({ status }: { status: RunStatus }) {
  return (
    <span
      className={cn(
        'inline-flex h-5 items-center gap-1.5 rounded-md px-1.5 text-xs font-medium whitespace-nowrap',
        status === 'succeeded' && 'bg-accent text-foreground',
        status === 'running' && 'bg-tint-blue/12 text-tint-blue',
        status !== 'succeeded' && status !== 'running' && 'bg-destructive/10 text-destructive',
      )}
    >
      <span
        className={cn(
          'size-1.5 rounded-full',
          status === 'succeeded'
            ? 'bg-tint-green'
            : status === 'running'
              ? 'bg-tint-blue animate-pulse'
              : 'bg-destructive',
        )}
        aria-hidden
      />
      {RUN_STATUS_LABELS[status]}
    </span>
  );
}

const shortThread = (id: string) => (id.length > 18 ? `${id.slice(0, 8)}…${id.slice(-6)}` : id);

function useAgents(tenantId: string) {
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  const list = agents.data?.pages.flatMap((p) => p.items) ?? [];
  return { list, name: (id: string) => list.find((a) => a.id === id)?.name ?? id.slice(0, 8) };
}

function FilterSelect<T extends string>({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-muted-foreground text-xs">
        {label}
      </Label>
      <Select value={value} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger id={id} aria-label={label} className="w-full sm:w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CallerLabel({ caller }: { caller: ThreadCaller | null }) {
  if (!caller) return <span className="text-muted-foreground">—</span>;
  const Icon = caller.type === 'api_key' ? KeyRound : User;
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <Icon className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
      <span className="truncate">
        {caller.name ?? (caller.type === 'api_key' ? 'Deleted API key' : 'Deleted user')}
      </span>
    </span>
  );
}

function LoadMore({
  query,
}: {
  query: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => unknown };
}) {
  if (!query.hasNextPage) return null;
  return (
    <Button
      variant="outline"
      className="justify-self-start"
      disabled={query.isFetchingNextPage}
      onClick={() => void query.fetchNextPage()}
    >
      Load more
    </Button>
  );
}

// --- List ---

function AgentRuns({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const agents = useAgents(tenantId);
  const [agentId, setAgentId] = useState<string>(ALL);
  const [status, setStatus] = useState<RunStatus | typeof ALL>(ALL);
  const [source, setSource] = useState<RunSource | typeof ALL>(ALL);
  const [range, setRange] = useState<keyof typeof RANGES>('all');
  // Fixed when the range is picked, so paging keeps a stable window.
  const [from, setFrom] = useState<string | null>(null);

  const params = new URLSearchParams();
  if (agentId !== ALL) params.set('agentId', agentId);
  if (status !== ALL) params.set('status', status);
  if (source !== ALL) params.set('source', source);
  if (from) params.set('from', from);
  const query = params.toString();
  const runs = usePaged<RunDto>(['runs', tenantId, query], `/runs${query ? `?${query}` : ''}`);
  const rows = runs.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
        <FilterSelect
          id="runs-agent"
          label="Agent"
          value={agentId}
          onChange={setAgentId}
          options={[
            { value: ALL, label: 'All agents' },
            ...agents.list.map((a) => ({ value: a.id, label: a.name })),
          ]}
        />
        <FilterSelect
          id="runs-status"
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: ALL, label: 'Any status' },
            ...STATUSES.map((s) => ({ value: s, label: s })),
          ]}
        />
        <FilterSelect
          id="runs-source"
          label="Source"
          value={source}
          onChange={setSource}
          options={[{ value: ALL, label: 'Any source' }, ...SOURCES]}
        />
        <FilterSelect
          id="runs-range"
          label="Started"
          value={range}
          onChange={(next) => {
            setRange(next);
            setFrom(next === 'all' ? null : new Date(Date.now() - RANGE_MS[next]).toISOString());
          }}
          options={Object.entries(RANGES).map(([value, label]) => ({
            value: value as keyof typeof RANGES,
            label,
          }))}
        />
      </div>
      <ErrorAlert error={runs.error} />
      {runs.isPending ? (
        <Skeleton className="h-24" />
      ) : rows.length === 0 ? (
        <EmptyState title="No runs">
          Every agent run, from the playground and from endpoints, produces a trace.
        </EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Started</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Thread</TableHead>
              <TableHead className="text-right">Duration</TableHead>
              <TableHead className="text-right">Tokens</TableHead>
              <TableHead className="text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.id}
                className="cursor-pointer"
                onClick={() =>
                  void navigate({ to: '/traces/runs/$runId', params: { runId: r.id } })
                }
              >
                <TableCell className="text-xs whitespace-nowrap">
                  <Link
                    to="/traces/runs/$runId"
                    params={{ runId: r.id }}
                    className="hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {formatDateTime(r.startedAt)}
                  </Link>
                </TableCell>
                <TableCell>
                  {agents.name(r.agentId)}{' '}
                  <span className="text-muted-foreground text-xs">v{r.version}</span>
                </TableCell>
                <TableCell>
                  <RunStatusBadge status={r.status} />
                </TableCell>
                <TableCell className="text-xs">{RUN_SOURCE_LABELS[r.source]}</TableCell>
                <TableCell className="font-mono text-xs">
                  {r.threadId ? (
                    <Link
                      to="/traces/threads"
                      search={threadSearch({ ...r, threadId: r.threadId })}
                      className="hover:underline"
                      title={r.threadId}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {shortThread(r.threadId)}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-xs tabular-nums">
                  {formatDuration(r.latencyMs)}
                </TableCell>
                <TableCell className="text-right text-xs tabular-nums">
                  {r.inputTokens === null
                    ? '—'
                    : formatNumber((r.inputTokens ?? 0) + (r.outputTokens ?? 0))}
                </TableCell>
                <TableCell className="text-right text-xs tabular-nums" title={COST_ESTIMATE_NOTE}>
                  {formatUsd(r.costUsd)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <LoadMore query={runs} />
    </div>
  );
}

function Threads({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const agents = useAgents(tenantId);
  const [agentId, setAgentId] = useState<string>(ALL);
  const [source, setSource] = useState<RunSource | typeof ALL>(ALL);
  const params = new URLSearchParams();
  if (agentId !== ALL) params.set('agentId', agentId);
  if (source !== ALL) params.set('source', source);
  const query = params.toString();
  const threads = usePaged<ThreadDto>(
    ['threads', tenantId, query],
    `/threads${query ? `?${query}` : ''}`,
  );
  const rows = threads.data?.pages.flatMap((p) => p.items) ?? [];
  const open = (t: ThreadDto) =>
    void navigate({
      to: '/traces/threads',
      search: threadSearch({
        ...t,
        apiKeyId: t.caller?.type === 'api_key' ? t.caller.id : null,
        userId: t.caller?.type === 'user' ? t.caller.id : null,
      }),
    });

  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
        <FilterSelect
          id="threads-agent"
          label="Agent"
          value={agentId}
          onChange={setAgentId}
          options={[
            { value: ALL, label: 'All agents' },
            ...agents.list.map((a) => ({ value: a.id, label: a.name })),
          ]}
        />
        <FilterSelect
          id="threads-source"
          label="Source"
          value={source}
          onChange={setSource}
          options={[{ value: ALL, label: 'Any source' }, ...SOURCES]}
        />
      </div>
      <ErrorAlert error={threads.error} />
      {threads.isPending ? (
        <Skeleton className="h-24" />
      ) : rows.length === 0 ? (
        <EmptyState title="No threads">
          A thread is one conversation. Agents with memory start one on the first request and return
          its <code>threadId</code>; send it back to continue the conversation.
        </EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Last active</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Thread</TableHead>
              <TableHead>Caller</TableHead>
              <TableHead>First message</TableHead>
              <TableHead className="text-right">Turns</TableHead>
              <TableHead className="text-right">Tokens</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead>Last status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow
                key={`${t.agentId}:${t.threadId}:${t.caller?.id ?? ''}`}
                className="cursor-pointer"
                onClick={() => open(t)}
                data-testid="thread-row"
              >
                <TableCell className="text-xs whitespace-nowrap">
                  {formatDateTime(t.lastAt)}
                </TableCell>
                <TableCell>{agents.name(t.agentId)}</TableCell>
                <TableCell className="font-mono text-xs" title={t.threadId}>
                  {shortThread(t.threadId)}
                </TableCell>
                <TableCell className="max-w-48 text-xs">
                  <CallerLabel caller={t.caller} />
                </TableCell>
                <TableCell className="text-muted-foreground max-w-72 truncate text-xs">
                  {t.preview || '—'}
                </TableCell>
                <TableCell className="text-right text-xs tabular-nums">{t.runCount}</TableCell>
                <TableCell className="text-right text-xs tabular-nums">
                  {formatNumber(t.inputTokens + t.outputTokens)}
                </TableCell>
                <TableCell
                  className="text-right text-xs tabular-nums"
                  title={costCell(t.costUsd, t.unpricedRuns).title ?? COST_ESTIMATE_NOTE}
                >
                  {costCell(t.costUsd, t.unpricedRuns).text}
                </TableCell>
                <TableCell>
                  <RunStatusBadge status={t.lastStatus} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <LoadMore query={threads} />
    </div>
  );
}

function WorkflowTraces({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const runs = usePaged<WorkflowRunDto>(['workflow-runs', tenantId, 'all'], '/workflow-runs');
  const rows = runs.data?.pages.flatMap((p) => p.items) ?? [];
  const search = useListSearch(rows, (r) => [r.workflowName, r.status]);
  return (
    <div className="grid gap-4">
      <ErrorAlert error={runs.error} />
      {runs.isPending ? (
        <Skeleton className="h-24" />
      ) : rows.length === 0 ? (
        <EmptyState title="No workflow runs yet">Run a workflow to see its trace here.</EmptyState>
      ) : (
        <>
          <ListSearch
            value={search.query}
            onChange={search.setQuery}
            label="Search workflow runs"
            placeholder="Search by workflow or status"
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>Workflow</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Steps</TableHead>
                <TableHead className="text-right">Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() =>
                    void navigate({ to: '/traces/workflows/$runId', params: { runId: r.id } })
                  }
                >
                  <TableCell className="text-xs whitespace-nowrap">
                    <Link
                      to="/traces/workflows/$runId"
                      params={{ runId: r.id }}
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {formatDateTime(r.startedAt)}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.workflowName}</TableCell>
                  <TableCell>
                    <Badge variant={WORKFLOW_STATUS_VARIANT[r.status]}>
                      {WORKFLOW_STATUS_LABEL[r.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.steps.filter((s) => s.status === 'succeeded').length}/{r.steps.length}{' '}
                    succeeded
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {formatDuration(r.latencyMs)}
                  </TableCell>
                </TableRow>
              ))}
              {search.filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <NoMatches query={search.query} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      )}
      <LoadMore query={runs} />
    </div>
  );
}

export function TracesPage() {
  const { tab = 'threads' } = useSearch({ from: '/app/traces' });
  const navigate = useNavigate();
  return (
    <>
      <PageHeader
        title="Traces"
        description="Every agent and workflow run with its span timeline (model calls, tool calls, errors), and the conversations (threads) they belong to. Payloads are stored redacted."
      />
      <RequireTenant>
        {(tenant) => (
          <Tabs
            value={tab}
            onValueChange={(next) =>
              void navigate({ to: '/traces', search: { tab: next as TraceTab }, replace: true })
            }
          >
            <TabsList>
              <TabsTrigger value="threads">Threads</TabsTrigger>
              <TabsTrigger value="runs">Agent runs</TabsTrigger>
              <TabsTrigger value="workflows">Workflow runs</TabsTrigger>
            </TabsList>
            <Card className="mt-2">
              <CardContent className="pt-6">
                <TabsContent value="runs">
                  <AgentRuns tenantId={tenant.id} />
                </TabsContent>
                <TabsContent value="threads">
                  <Threads tenantId={tenant.id} />
                </TabsContent>
                <TabsContent value="workflows">
                  <WorkflowTraces tenantId={tenant.id} />
                </TabsContent>
              </CardContent>
            </Card>
          </Tabs>
        )}
      </RequireTenant>
    </>
  );
}

// --- Detail ---

/** Agent run → spans: the run, tool calls (call → result), and other recorded steps. */
export function runSpans(run: RunDetailDto): { spans: Span[]; totalMs: number } {
  const start = ms(run.startedAt);
  const end = run.endedAt ? ms(run.endedAt) : Date.now();
  const totalMs = Math.max(end - start, 1);
  const spans: Span[] = [
    {
      id: 'run',
      name: run.model,
      kind: 'run',
      depth: 0,
      startMs: 0,
      endMs: run.endedAt ? totalMs : null,
      status: run.status === 'running' ? 'running' : run.status === 'succeeded' ? 'ok' : 'error',
      attributes: {
        status: run.status,
        source: run.source,
        version: run.version,
        model: run.model,
        provider: run.providerType,
        inputTokens: run.inputTokens,
        outputTokens: run.outputTokens,
        ...(run.input.requestContext ? { requestContext: run.input.requestContext } : {}),
        ...(run.error ? { error: run.error } : {}),
      },
    },
  ];
  const results = new Map(
    run.steps
      .filter((s) => s.type === 'tool_result')
      .map((s) => [String(s.payload.id), s] as const),
  );
  for (const step of run.steps) {
    if (step.type === 'tool_result') continue;
    const offset = ms(step.startedAt) - start;
    if (step.type === 'tool_call') {
      const result = results.get(String(step.payload.id));
      spans.push({
        id: step.id,
        name: step.name,
        kind: 'tool',
        depth: 1,
        startMs: offset,
        endMs: result ? ms(result.startedAt) - start : null,
        status: !result
          ? run.status === 'running'
            ? 'running'
            : 'error'
          : result.payload.ok === false
            ? 'error'
            : 'ok',
        attributes: {
          args: step.payload.args,
          ...(result ? { ok: result.payload.ok, output: result.payload.output } : {}),
        },
      });
    } else {
      spans.push({
        id: step.id,
        name: step.name,
        kind: step.type === 'error' ? 'error' : step.type.replace('_', ' '),
        depth: 1,
        startMs: offset,
        endMs: step.endedAt ? ms(step.endedAt) - start : offset,
        status: step.type === 'error' ? 'error' : 'ok',
        attributes: step.payload,
      });
    }
  }
  return { spans, totalMs };
}

function workflowSpans(run: WorkflowRunDto): { spans: Span[]; totalMs: number } {
  const topLevel = new Set(run.definition.graph.map((e) => e.id));
  const start = ms(run.startedAt);
  const end = run.endedAt ? ms(run.endedAt) : Date.now();
  const totalMs = Math.max(end - start, 1);
  return {
    totalMs,
    spans: [
      {
        id: 'workflow',
        name: run.workflowName,
        kind: 'workflow',
        depth: 0,
        startMs: 0,
        endMs: run.endedAt ? totalMs : null,
        status:
          run.status === 'succeeded'
            ? 'ok'
            : run.status === 'failed' || run.status === 'cancelled'
              ? 'error'
              : 'running',
        attributes: {
          status: run.status,
          trigger: run.trigger,
          ...(run.parentRunId ? { parentRunId: run.parentRunId } : {}),
          ...(run.timeTravel ? { timeTravel: run.timeTravel } : {}),
          input: run.input,
          ...(Object.keys(run.requestContext).length ? { requestContext: run.requestContext } : {}),
          output: run.output,
          ...(run.error ? { error: run.error } : {}),
        },
      },
      ...run.steps.map((s) => ({
        id: s.stepId,
        name: s.name,
        kind: s.type,
        // Steps inside a parallel, branch, loop or foreach sit under their container.
        depth: topLevel.has(s.stepId) ? 1 : 2,
        startMs: s.startedAt ? ms(s.startedAt) - start : 0,
        endMs: s.endedAt
          ? ms(s.endedAt) - start
          : s.status === 'running'
            ? null
            : s.startedAt
              ? ms(s.startedAt) - start
              : 0,
        status: (s.status === 'succeeded'
          ? 'ok'
          : s.status === 'failed'
            ? 'error'
            : s.status === 'running'
              ? 'running'
              : s.status === 'suspended' || s.status === 'waiting'
                ? 'running'
                : 'skipped') as Span['status'],
        attributes: {
          status: s.status,
          ...(s.input !== undefined ? { input: s.input } : {}),
          ...(s.output !== undefined ? { output: s.output } : {}),
          ...(s.error ? { error: s.error } : {}),
          ...(s.runId ? { agentRunId: s.runId } : {}),
          ...(s.childRunId ? { nestedWorkflowRunId: s.childRunId } : {}),
          ...(s.iterations !== undefined ? { iterations: s.iterations } : {}),
          ...(s.items ? { items: `${s.items.done}/${s.items.total}` } : {}),
          ...(s.resumeAt ? { resumeAt: s.resumeAt } : {}),
          ...(s.message ? { message: s.message } : {}),
        },
      })),
    ],
  };
}

function SpanDetails({ span }: { span: Span | undefined }) {
  if (!span) return <p className="text-muted-foreground text-sm">Select a span.</p>;
  const kind =
    span.kind === 'tool'
      ? 'tool'
      : span.kind === 'model call'
        ? 'model'
        : span.kind === 'error' || span.status === 'error'
          ? 'error'
          : span.kind === 'run' || span.kind === 'workflow'
            ? 'agent'
            : 'step';
  return (
    <div className="grid gap-4 text-sm">
      <div className="grid gap-1.5">
        <RoleTag role={kind}>{span.kind}</RoleTag>
        <div className="font-mono font-medium break-all">{span.name}</div>
        <div className="text-muted-foreground text-xs tabular-nums">
          starts +{formatDuration(span.startMs)} ·{' '}
          {span.endMs === null ? 'running' : formatDuration(span.endMs - span.startMs)}
        </div>
      </div>
      {typeof span.attributes.agentRunId === 'string' && (
        <Button size="sm" variant="outline" className="justify-self-start" asChild>
          <Link to="/traces/runs/$runId" params={{ runId: span.attributes.agentRunId }}>
            Open the agent run’s trace
          </Link>
        </Button>
      )}
      <Facts
        rows={Object.entries(span.attributes)
          .filter(([, v]) => v !== undefined && (v === null || typeof v !== 'object'))
          .map(([key, value]) => [
            key,
            <span className="font-mono text-xs" title={value === null ? undefined : String(value)}>
              {value === null ? '—' : String(value)}
            </span>,
          ])}
      />
      {requestOf(span) && <RequestMessages messages={requestOf(span)!} />}
      {responseOf(span)?.text && (
        <div className="grid min-w-0 gap-1">
          <span className="text-muted-foreground text-xs">Model reply</span>
          <JsonBlock value={responseOf(span)!.text} />
        </div>
      )}
      {Object.entries(span.attributes)
        .filter(
          ([key, v]) =>
            key !== 'request' && key !== 'response' && v !== null && typeof v === 'object',
        )
        .map(([key, value]) => (
          <div key={key} className="grid gap-1">
            <span className="text-muted-foreground text-xs">{key}</span>
            <JsonBlock value={value} />
          </div>
        ))}
    </div>
  );
}

function RunScores({
  tenantId,
  runId,
  finished,
}: {
  tenantId: string;
  runId: string;
  finished: boolean;
}) {
  const me = useMe();
  const queryClient = useQueryClient();
  const canEdit = Boolean(me.activeTenant && hasRole(me.activeTenant.role, 'admin'));
  const scores = useQuery({
    queryKey: ['run-scores', tenantId, runId],
    queryFn: () => api<{ items: ScoreDto[] }>(`/runs/${runId}/scores`),
  });
  const scorers = useQuery({
    queryKey: ['scorers', tenantId],
    queryFn: () => api<{ items: ScorerDto[] }>('/scorers'),
    enabled: canEdit,
  });
  const [scorerId, setScorerId] = useState('');
  const score = useMutation({
    mutationFn: () =>
      api<ScoreDto>(`/runs/${runId}/scores`, { method: 'POST', body: { scorerId } }),
    onSuccess: (s) => {
      toast.success(`${s.scorerName}: ${Math.round(s.score * 100)}%`);
      void queryClient.invalidateQueries({ queryKey: ['run-scores', tenantId, runId] });
      void queryClient.invalidateQueries({ queryKey: ['scorers', tenantId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <PanelSection title="Scores">
      <div className="grid gap-3">
        <ErrorAlert error={scores.error} />
        {(scores.data?.items.length ?? 0) === 0 ? (
          <p className="text-muted-foreground text-sm">Not scored yet.</p>
        ) : (
          <ul className="grid gap-2">
            {scores.data?.items.map((s) => (
              <li key={s.id} className="grid gap-0.5 text-sm" data-testid="run-score">
                <span className="flex items-center gap-2">
                  <span className="font-mono text-xs">{s.scorerName}</span>
                  <ScoreBar score={s.score} />
                  {s.source === 'manual' && <Badge variant="outline">manual</Badge>}
                </span>
                {s.reason && <span className="text-muted-foreground text-xs">{s.reason}</span>}
              </li>
            ))}
          </ul>
        )}
        {canEdit && finished && (scorers.data?.items.length ?? 0) > 0 && (
          <div className="flex gap-2">
            <Select value={scorerId} onValueChange={setScorerId}>
              <SelectTrigger className="w-48" aria-label="Scorer to run">
                <SelectValue placeholder="Choose a scorer" />
              </SelectTrigger>
              <SelectContent>
                {scorers.data?.items.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button disabled={!scorerId || score.isPending} onClick={() => score.mutate()}>
              {score.isPending ? 'Scoring…' : 'Score'}
            </Button>
          </div>
        )}
      </div>
    </PanelSection>
  );
}

function TraceLayout({
  spans,
  totalMs,
  side,
  selected: controlled,
  onSelect,
  stacked = false,
}: {
  spans: Span[];
  totalMs: number;
  side?: React.ReactNode;
  /** Controlled selection (e.g. shared with an event timeline); local state otherwise. */
  selected?: string | null;
  onSelect?: (id: string) => void;
  /** One column with a compact waterfall, for narrow spots like a thread turn. */
  stacked?: boolean;
}) {
  const [local, setLocal] = useState<string | null>(spans[0]?.id ?? null);
  const selected = controlled !== undefined ? controlled : local;
  const span = spans.find((s) => s.id === selected) ?? spans[0];
  return (
    <div className={cn('grid gap-4', !stacked && 'xl:grid-cols-[minmax(0,1fr)_24rem]')}>
      <section className="bg-surface self-start overflow-x-auto rounded-xl border p-4">
        <h3 className="text-muted-foreground mb-3 flex items-center gap-2 text-xs font-medium">
          <ListTree className="size-3.5" aria-hidden /> Spans
        </h3>
        <TraceWaterfall
          spans={spans}
          totalMs={totalMs}
          selected={span?.id ?? null}
          onSelect={onSelect ?? setLocal}
          compact={stacked}
        />
      </section>
      <div className="grid content-start gap-4">
        <section className="bg-surface rounded-xl border p-4">
          <SpanDetails span={span} />
        </section>
        {side}
      </div>
    </div>
  );
}

const isFinal = (status: RunStatus) => status !== 'running';

function useRunDetail(tenantId: string, runId: string) {
  return useQuery({
    queryKey: ['run', tenantId, runId],
    queryFn: () => api<RunDetailDto>(`/runs/${runId}`),
    refetchInterval: (q) => (q.state.data && !isFinal(q.state.data.status) ? 2000 : false),
  });
}

/** A run still "running" after this long has most likely lost its worker. */
const STALLED_MS = 15 * 60_000;

const runTone = (status: RunStatus) =>
  status === 'running' ? 'running' : status === 'succeeded' ? 'idle' : 'error';

const preview = (text: string) =>
  (text.length > 60 ? `${text.slice(0, 60)}…` : text) || '(no text)';

/** Matches a transcript item against the search, case-insensitively; empty search matches all. */
const matcher = (query: string) => {
  const q = query.trim().toLowerCase();
  return (...texts: Array<string | null | undefined>) =>
    !q || texts.some((t) => t?.toLowerCase().includes(q));
};

const spanKind = (span: Span): TimelineKind =>
  span.kind === 'tool'
    ? 'tool'
    : span.kind === 'model call'
      ? 'model'
      : span.kind === 'error'
        ? 'error'
        : 'step';

/** The page's two-pane workspace: header on top, the timeline and transcript left, a panel right. */
function SessionWorkspace({
  header,
  main,
  panel,
}: {
  header: React.ReactNode;
  main: React.ReactNode;
  panel: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5 lg:h-[calc(100dvh-6.5rem)]">
      <header className="grid gap-3">{header}</header>
      <div className="grid min-h-0 flex-1 gap-5 lg:grid-cols-[minmax(0,1fr)_24rem] xl:grid-cols-[minmax(0,1fr)_27rem]">
        <section className="flex min-h-0 min-w-0 flex-col" aria-label="Transcript">
          {main}
        </section>
        <aside
          className="bg-surface flex min-h-0 flex-col overflow-hidden rounded-xl border"
          aria-label="Details"
        >
          {panel}
        </aside>
      </div>
    </div>
  );
}

function PanelTabs<T extends string>({
  value,
  onChange,
  tabs,
}: {
  value: T;
  onChange: (value: T) => void;
  tabs: Array<{ value: T; label: string; content: React.ReactNode; disabled?: boolean }>;
}) {
  return (
    <Tabs
      value={value}
      onValueChange={(v) => onChange(v as T)}
      className="flex min-h-0 flex-1 flex-col gap-0"
    >
      <TabsList className="w-full flex-nowrap overflow-x-auto border-b px-2 py-2">
        {tabs.map((t) => (
          <TabsTrigger key={t.value} value={t.value} disabled={t.disabled}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((t) => (
        <TabsContent
          key={t.value}
          value={t.value}
          className="grid min-h-0 flex-1 auto-rows-min content-start grid-cols-[minmax(0,1fr)] gap-5 overflow-x-hidden overflow-y-auto p-5 [scrollbar-width:thin]"
        >
          {t.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}

/** Scrolls the transcript to an event and flashes it. */
function revealEvent(id: string) {
  document.getElementById(`evt-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function MessageBody({ message }: { message: ChatMessage }) {
  return (
    <div className="grid min-w-0 gap-2 px-4 py-3 [overflow-wrap:anywhere]">
      {message.attachments && message.attachments.length > 0 && (
        <MessageAttachments attachments={message.attachments} />
      )}
      {message.content &&
        (message.role === 'user' ? (
          <p className="text-sm [overflow-wrap:anywhere] whitespace-pre-wrap">{message.content}</p>
        ) : (
          <Markdown>{message.content}</Markdown>
        ))}
    </div>
  );
}

/** The prompt sent to the model for a call, when the runner recorded it. */
function requestOf(span: Span): Array<{ role: string; content: string }> | null {
  const request = span.attributes.request;
  return Array.isArray(request) && request.length > 0
    ? (request as Array<{ role: string; content: string }>)
    : null;
}

/** What the model answered for a call: its text and the tools it asked for. */
function responseOf(span: Span): { text?: string; toolCalls?: unknown[] } | null {
  const r = span.attributes.response;
  return r && typeof r === 'object' ? (r as { text?: string; toolCalls?: unknown[] }) : null;
}

const isScalar = (v: unknown) => v === null || typeof v !== 'object';

/** A tool call, model call or step, collapsed to one line with its payload one click away. */
function SpanBody({ span }: { span: Span }) {
  const request = requestOf(span);
  const response = responseOf(span);
  const isModel = span.kind === 'model call';
  const entries = Object.entries(span.attributes).filter(
    ([key, v]) => v !== undefined && key !== 'request' && key !== 'response',
  );
  const scalars = entries.filter(([, v]) => isScalar(v));
  const objects = entries.filter(([, v]) => !isScalar(v));
  if (entries.length === 0 && !request && !response) return null;
  const summary = (label: string) => (
    <summary className="text-muted-foreground hover:text-foreground flex cursor-pointer list-none items-center gap-1.5 px-4 py-2.5 text-xs transition-colors select-none [&::-webkit-details-marker]:hidden">
      <ChevronRight
        className="size-3.5 transition-transform duration-200 group-open:rotate-90"
        aria-hidden
      />
      {label}
    </summary>
  );
  const section = (label: string, body: ReactNode, open: boolean, first = false) => (
    <details className={cn('group', !first && 'border-t')} open={open}>
      {summary(label)}
      <div className="border-t px-4 py-3">{body}</div>
    </details>
  );
  return (
    <div className="grid min-w-0">
      {isModel &&
        (request ? (
          section(
            `Input — sent to the model (${request.length} messages)`,
            <RequestMessages messages={request} />,
            true,
            true,
          )
        ) : (
          <p className="text-muted-foreground px-4 py-3 text-xs">
            The prompt wasn’t recorded for this run. Runs made after the latest update include it.
          </p>
        ))}
      {response &&
        section(
          'Output — model reply',
          <div className="grid min-w-0 gap-3">
            {response.text ? (
              <JsonBlock value={response.text} />
            ) : (
              <p className="text-muted-foreground text-xs">
                No text — the model only called tools.
              </p>
            )}
            {response.toolCalls?.map((call, i) => (
              <div key={i} className="grid min-w-0 gap-1">
                <span className="text-muted-foreground text-xs">
                  Requested tool{' '}
                  <span className="text-foreground font-mono">
                    {String((call as { name?: unknown }).name)}
                  </span>
                </span>
                <JsonBlock value={(call as { args?: unknown }).args} />
              </div>
            ))}
          </div>,
          true,
          !request && !isModel,
        )}
      {(scalars.length > 0 || objects.length > 0) &&
        (isModel ? (
          <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 border-t px-4 py-2.5 text-xs">
            {scalars.map(([key, value]) => (
              <span key={key}>
                {key} <span className="text-foreground font-mono">{String(value ?? '—')}</span>
              </span>
            ))}
          </div>
        ) : (
          <details className={cn('group', (request || response) && 'border-t')}>
            {summary(
              span.kind === 'tool'
                ? span.attributes.ok === false
                  ? 'Failed — show input and output'
                  : span.endMs === null
                    ? 'Running — show input'
                    : 'Show input and output'
                : 'Show details',
            )}
            <div className="grid gap-3 border-t px-4 py-3">
              {entries.map(([key, value]) => (
                <div key={key} className="grid min-w-0 gap-1">
                  <span className="text-muted-foreground text-xs">{key}</span>
                  <JsonBlock value={value} />
                </div>
              ))}
            </div>
          </details>
        ))}
    </div>
  );
}

function RunTrace({ tenantId, runId }: { tenantId: string; runId: string }) {
  const agents = useAgents(tenantId);
  const run = useRunDetail(tenantId, runId);
  const trace = useMemo(() => (run.data ? runSpans(run.data) : null), [run.data]);
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'session' | 'event' | 'spans' | 'tools' | 'scores'>('session');
  const [query, setQuery] = useState('');
  const [spanId, setSpanId] = useState<string | null>(null);

  if (run.error) return <ErrorAlert error={run.error} />;
  if (!run.data || !trace) return <SessionSkeleton />;
  const r = run.data;
  const agentName = agents.name(r.agentId);
  const startAt = ms(r.startedAt);
  const [runSpan, ...childSpans] = trace.spans;
  const steps = [...childSpans].sort((a, b) => a.startMs - b.startMs);
  let lastUser = -1;
  r.input.messages.forEach((m, i) => {
    if (m.role === 'user') lastUser = i;
  });
  const userText = lastUser >= 0 ? (r.input.messages[lastUser]?.content ?? '') : '';

  const events: TimelineEvent[] = [
    ...(lastUser >= 0
      ? [{ id: 'input', kind: 'user' as const, label: preview(userText), startMs: 0, endMs: 0 }]
      : []),
    {
      id: 'run',
      kind: 'agent',
      label: `${agentName} · ${r.model}`,
      startMs: 0,
      endMs: runSpan?.endMs ?? null,
      failed: r.status !== 'running' && r.status !== 'succeeded',
    },
    ...steps.map((s) => ({
      id: s.id,
      kind: spanKind(s),
      label: s.name,
      startMs: s.startMs,
      endMs: s.endMs,
      failed: s.status === 'error',
    })),
  ];

  const select = (id: string) => {
    setSelected(id);
    setTab('event');
    revealEvent(id);
  };
  /** Jump to a tool call in the transcript without leaving the Tools tab. */
  const selectTool = (id: string) => {
    setSelected(id);
    revealEvent(id);
  };
  const matches = matcher(query);
  const matchCount = query.trim()
    ? r.input.messages.filter((m) => matches(m.content)).length +
      steps.filter((s) => matches(s.name, JSON.stringify(s.attributes))).length +
      (r.output && matches(r.output.text) ? 1 : 0)
    : null;

  const modelSteps = r.steps.filter((s) => s.type === 'model_call');
  const tokenPoints = modelSteps.some((s) => typeof s.payload.outputTokens === 'number')
    ? modelSteps.map((s) => ({
        at: ms(s.endedAt ?? s.startedAt),
        value: Number(s.payload.inputTokens ?? 0) + Number(s.payload.outputTokens ?? 0),
      }))
    : [
        {
          at: ms(r.endedAt ?? r.startedAt),
          value: (r.inputTokens ?? 0) + (r.outputTokens ?? 0),
        },
      ];
  const toolCount = r.steps.filter((s) => s.type === 'tool_call').length;
  const largestPrompt = Math.max(0, ...modelSteps.map((s) => Number(s.payload.inputTokens ?? 0)));
  const selectedSpan = trace.spans.find((s) => s.id === (selected === 'input' ? null : selected));

  return (
    <>
      <PageHeader
        title={`Trace · ${agentName}`}
        breadcrumb={[{ label: 'Traces', to: '/traces' }]}
        actions={
          <>
            {r.threadId && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/traces/threads" search={threadSearch({ ...r, threadId: r.threadId })}>
                  <MessagesSquare aria-hidden /> Open thread
                </Link>
              </Button>
            )}
            <Button variant="ghost" size="sm" asChild>
              <Link to="/traces">All traces</Link>
            </Button>
          </>
        }
      />
      <SessionWorkspace
        header={
          <>
            <SessionTitle
              id={r.id}
              status={<StatusPill label={RUN_STATUS_LABELS[r.status]} tone={runTone(r.status)} />}
            />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
              <SessionChip
                icon={Bot}
                label={`${agentName} · v${r.version}`}
                title="Agent"
                link={{ to: '/agents/$agentId', params: { agentId: r.agentId } }}
              />
              <SessionChip icon={Cpu} label={r.model} title={`Model (${r.providerType})`} />
              <SessionChip icon={Radio} label={RUN_SOURCE_LABELS[r.source]} title="Source" />
              {r.threadId && (
                <SessionChip
                  icon={MessagesSquare}
                  label={shortThread(r.threadId)}
                  title={`Thread ${r.threadId}`}
                  link={{
                    to: '/traces/threads',
                    search: threadSearch({ ...r, threadId: r.threadId }) as unknown as Record<
                      string,
                      string
                    >,
                  }}
                />
              )}
              <div className="text-foreground flex flex-wrap items-center gap-x-5 gap-y-2 sm:ml-3">
                <SessionMetric icon={Timer} label="Duration">
                  {formatDuration(r.latencyMs ?? (r.status === 'running' ? trace.totalMs : null))}
                </SessionMetric>
                <SessionMetric icon={Layers} label="Tokens in / out">
                  {r.inputTokens === null
                    ? '—'
                    : `${formatCompact(r.inputTokens)} / ${formatCompact(r.outputTokens ?? 0)}`}
                </SessionMetric>
                <SessionMetric icon={CircleDollarSign} label="Cost">
                  <span title={COST_ESTIMATE_NOTE}>{formatUsd(r.costUsd)}</span>
                </SessionMetric>
                <SessionMetric icon={Wrench} label="Tool calls">
                  {toolCount}
                </SessionMetric>
                <SessionMetric icon={Clock} label="Started">
                  <span title={formatDateTime(r.startedAt)}>{formatRelative(r.startedAt)}</span>
                </SessionMetric>
              </div>
            </div>
          </>
        }
        main={
          <>
            <EventTimeline
              events={events}
              startAt={startAt}
              totalMs={trace.totalMs}
              selected={selected}
              onSelect={select}
              toolbar={<TranscriptSearch value={query} onChange={setQuery} matches={matchCount} />}
            />
            <TranscriptScroll>
              {r.input.messages.map((m, i) => (
                <TranscriptItem
                  key={i}
                  id={i === lastUser ? 'input' : `msg-${i}`}
                  role={m.role === 'user' ? 'user' : 'agent'}
                  tag={m.role === 'user' ? 'User' : agentName}
                  at={r.startedAt}
                  selected={i === lastUser && selected === 'input'}
                  dimmed={!matches(m.content)}
                  onSelect={() => i === lastUser && select('input')}
                  extra={i < lastUser ? <span>earlier message</span> : undefined}
                >
                  <MessageBody message={m} />
                </TranscriptItem>
              ))}
              {steps.map((s) => (
                <TranscriptItem
                  key={s.id}
                  id={s.id}
                  role={spanKind(s)}
                  tag={
                    <span className="inline-flex items-center gap-1">
                      <span className="opacity-70">{KIND_TAG[spanKind(s)]}</span>
                      <span className="truncate font-mono">{s.name}</span>
                    </span>
                  }
                  at={startAt + s.startMs}
                  selected={selected === s.id}
                  dimmed={!matches(s.name, JSON.stringify(s.attributes))}
                  onSelect={() => select(s.id)}
                  extra={
                    <>
                      {spanKind(s) === 'model' && (
                        <TokenTag
                          input={numberOrNull(s.attributes.inputTokens)}
                          output={numberOrNull(s.attributes.outputTokens)}
                        />
                      )}
                      <span className="tabular-nums">
                        {s.endMs === null ? 'running' : formatDuration(s.endMs - s.startMs)}
                      </span>
                    </>
                  }
                >
                  <SpanBody span={s} />
                </TranscriptItem>
              ))}
              {r.output && (
                <TranscriptItem
                  id="run"
                  role="agent"
                  tag={agentName}
                  at={r.endedAt ?? r.startedAt}
                  selected={selected === 'run'}
                  dimmed={!matches(r.output.text)}
                  onSelect={() => select('run')}
                  testId="run-output"
                  extra={
                    <>
                      <TokenTag input={r.inputTokens} output={r.outputTokens} />
                      <span className="tabular-nums">{formatDuration(r.latencyMs)}</span>
                    </>
                  }
                >
                  <MessageBody message={{ role: 'assistant', content: r.output.text }} />
                </TranscriptItem>
              )}
              {r.error && (
                <TranscriptItem
                  id="run-error"
                  role="error"
                  tag="Error"
                  at={r.endedAt ?? r.startedAt}
                >
                  <p className="px-4 py-3 text-sm">
                    <span className="text-destructive font-mono text-xs">{r.error.code}</span> —{' '}
                    {r.error.message}
                  </p>
                </TranscriptItem>
              )}
              {r.status === 'running' &&
                (trace.totalMs > STALLED_MS ? (
                  <p className="text-muted-foreground bg-muted/50 rounded-lg px-3 py-2.5 text-sm">
                    Still marked running after {formatDuration(trace.totalMs)}, with no end
                    recorded. If nothing new appears, the worker running it may have stopped.
                  </p>
                ) : (
                  <p className="text-muted-foreground flex items-center gap-2 text-sm">
                    <span
                      className="bg-tint-blue size-1.5 animate-pulse rounded-full"
                      aria-hidden
                    />
                    Running…
                  </p>
                ))}
            </TranscriptScroll>
          </>
        }
        panel={
          <PanelTabs
            value={tab}
            onChange={setTab}
            tabs={[
              {
                value: 'session',
                label: 'Session',
                content: (
                  <>
                    <PanelSection title="Run">
                      <Facts
                        rows={[
                          [
                            'ID',
                            <span className="font-mono text-xs" title={r.id}>
                              {r.id}
                            </span>,
                          ],
                          [
                            'Status',
                            <StatusPill
                              label={RUN_STATUS_LABELS[r.status]}
                              tone={runTone(r.status)}
                            />,
                          ],
                          ['Started', formatDateTime(r.startedAt)],
                          ['Ended', r.endedAt ? formatDateTime(r.endedAt) : '—'],
                          [
                            'Agent',
                            <Link
                              to="/agents/$agentId"
                              params={{ agentId: r.agentId }}
                              className="underline decoration-dotted underline-offset-4"
                            >
                              {agentName} v{r.version}
                            </Link>,
                          ],
                          ['Model', <span className="font-mono text-xs">{r.model}</span>],
                          ['Provider', r.providerType],
                          ['Source', RUN_SOURCE_LABELS[r.source]],
                          [
                            'Thread',
                            r.threadId ? (
                              <Link
                                to="/traces/threads"
                                search={threadSearch({ ...r, threadId: r.threadId })}
                                className="font-mono text-xs underline decoration-dotted underline-offset-4"
                                title={r.threadId}
                              >
                                {r.threadId}
                              </Link>
                            ) : (
                              '—'
                            ),
                          ],
                        ]}
                      />
                    </PanelSection>
                    {r.input.requestContext && (
                      <PanelSection title="Request context">
                        <JsonBlock value={r.input.requestContext} />
                      </PanelSection>
                    )}
                    <PanelSection
                      title="Tokens"
                      aside={formatNumber((r.inputTokens ?? 0) + (r.outputTokens ?? 0))}
                    >
                      <CumulativeChart
                        label="Cumulative tokens over the run"
                        points={tokenPoints}
                        startAt={startAt}
                        endAt={startAt + trace.totalMs}
                      />
                    </PanelSection>
                    <PanelSection title="Usage">
                      <Facts
                        rows={[
                          [
                            modelSteps.length > 1 ? 'Input (all calls)' : 'Input tokens',
                            <span
                              title={
                                modelSteps.length > 1
                                  ? 'Every model call resends the whole prompt, so this is the sum across calls.'
                                  : undefined
                              }
                            >
                              {formatNumber(r.inputTokens ?? 0)}
                            </span>,
                          ],
                          ...((largestPrompt > 0 && modelSteps.length > 1
                            ? [['Prompt size', `${formatNumber(largestPrompt)} per call`]]
                            : []) as Array<[string, ReactNode]>),
                          ['Output tokens', formatNumber(r.outputTokens ?? 0)],
                          ...(((r.cacheReadTokens ?? 0) > 0
                            ? [['Cached input', formatNumber(r.cacheReadTokens ?? 0)]]
                            : []) as Array<[string, ReactNode]>),
                          ...(((r.cacheWriteTokens ?? 0) > 0
                            ? [['Cache writes', formatNumber(r.cacheWriteTokens ?? 0)]]
                            : []) as Array<[string, ReactNode]>),
                          ...(((r.cacheReadTokens ?? 0) > 0 || (r.cacheWriteTokens ?? 0) > 0
                            ? [
                                [
                                  'Cache hit rate',
                                  formatHitRate(r.cacheReadTokens ?? 0, r.inputTokens ?? 0),
                                ],
                              ]
                            : []) as Array<[string, ReactNode]>),
                          ['Cost', <span title={COST_ESTIMATE_NOTE}>{formatUsd(r.costUsd)}</span>],
                          ['Model calls', formatNumber(modelSteps.length)],
                          ['Tool calls', formatNumber(toolCount)],
                          ['Latency', formatDuration(r.latencyMs)],
                        ]}
                      />
                    </PanelSection>
                  </>
                ),
              },
              {
                value: 'event',
                label: 'Event',
                content:
                  selected === 'input' ? (
                    <PanelSection title="User message">
                      <JsonBlock value={userText} />
                    </PanelSection>
                  ) : selectedSpan ? (
                    <SpanDetails span={selectedSpan} />
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      Select an event on the timeline or in the transcript.
                    </p>
                  ),
              },
              {
                value: 'spans',
                label: 'Spans',
                content: (
                  <>
                    <div className="overflow-x-auto">
                      <TraceWaterfall
                        spans={trace.spans}
                        totalMs={trace.totalMs}
                        selected={spanId ?? trace.spans[0]?.id ?? null}
                        onSelect={setSpanId}
                        compact
                      />
                    </div>
                    <div className="border-t pt-5">
                      <SpanDetails
                        span={trace.spans.find((s) => s.id === spanId) ?? trace.spans[0]}
                      />
                    </div>
                  </>
                ),
              },
              {
                value: 'tools',
                label: toolCount ? `Tools ${toolCount}` : 'Tools',
                content: (
                  <ToolsPanel spans={trace.spans} selected={selected} onSelect={selectTool} />
                ),
              },
              {
                value: 'scores',
                label: 'Scores',
                content: (
                  <RunScores tenantId={tenantId} runId={runId} finished={isFinal(r.status)} />
                ),
              },
            ]}
          />
        }
      />
    </>
  );
}

/** Tools the agent called in this run, grouped by name; a call jumps to its place in the transcript. */
function ToolsPanel({
  spans,
  selected,
  onSelect,
}: {
  spans: Span[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const calls = spans.filter((s) => s.kind === 'tool').sort((a, b) => a.startMs - b.startMs);
  const groups = new Map<string, Span[]>();
  for (const c of calls) groups.set(c.name, [...(groups.get(c.name) ?? []), c]);
  const q = filter.trim().toLowerCase();
  const rows = [...groups].filter(([name]) => name.toLowerCase().includes(q));
  const failed = calls.filter((c) => c.status === 'error').length;
  const duration = (c: Span) => (c.endMs === null ? null : c.endMs - c.startMs);
  const toggle = (name: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(name)) next.add(name);
      return next;
    });

  if (!calls.length)
    return <p className="text-muted-foreground text-sm">No tools were called in this run.</p>;

  return (
    <div className="grid gap-3">
      <Input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter tools"
        aria-label="Filter tools"
      />
      <ul className="grid gap-2">
        {rows.map(([name, list]) => {
          const bad = list.filter((c) => c.status === 'error').length;
          const times = list.map(duration).filter((d): d is number => d !== null);
          const expanded = open.has(name);
          return (
            <li key={name} className="rounded-lg border">
              <button
                type="button"
                onClick={() => toggle(name)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                aria-expanded={expanded}
              >
                {expanded ? (
                  <ChevronDown className="size-3.5 shrink-0" aria-hidden />
                ) : (
                  <ChevronRight className="size-3.5 shrink-0" aria-hidden />
                )}
                <Wrench className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
                <span className="truncate font-mono">{name}</span>
                <span className="text-muted-foreground ml-auto flex shrink-0 items-center gap-2 text-xs tabular-nums">
                  {bad > 0 && <span className="text-destructive">{bad} failed</span>}
                  {times.length > 0 && formatDuration(times.reduce((a, b) => a + b, 0))}
                  <Badge variant="secondary">{list.length}×</Badge>
                </span>
              </button>
              {expanded && (
                <ul className="grid gap-1 border-t p-2">
                  {list.map((c, i) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => onSelect(c.id)}
                        className={cn(
                          'hover:bg-muted flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs',
                          selected === c.id && 'bg-muted',
                        )}
                      >
                        <span className="text-muted-foreground w-5 tabular-nums">#{i + 1}</span>
                        <span
                          className={cn(
                            c.status === 'error' && 'text-destructive',
                            c.status === 'running' && 'text-muted-foreground',
                          )}
                        >
                          {c.status === 'error'
                            ? 'failed'
                            : c.status === 'running'
                              ? 'running'
                              : 'ok'}
                        </span>
                        <span className="text-muted-foreground ml-auto tabular-nums">
                          +{formatDuration(c.startMs)} ·{' '}
                          {duration(c) === null ? '…' : formatDuration(duration(c))}
                        </span>
                      </button>
                      {selected === c.id && (
                        <div className="grid gap-2 px-2 pb-2">
                          <JsonBlock value={c.attributes.args} />
                          {c.attributes.output !== undefined && (
                            <JsonBlock value={c.attributes.output} />
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-muted-foreground border-t pt-2 text-xs">
        {groups.size} {groups.size === 1 ? 'tool' : 'tools'}, {calls.length}{' '}
        {calls.length === 1 ? 'call' : 'calls'}, {failed} failed
      </p>
    </div>
  );
}

const KIND_TAG: Record<TimelineKind, string> = {
  user: 'User',
  agent: 'Agent',
  model: 'Model',
  tool: 'Tool',
  step: 'Step',
  error: 'Error',
};

const numberOrNull = (v: unknown) => (typeof v === 'number' ? v : null);

function SessionSkeleton() {
  return (
    <div className="grid gap-5">
      <Skeleton className="h-8 w-96 max-w-full" />
      <div className="flex gap-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-8 w-28" />
      </div>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="grid content-start gap-4">
          <Skeleton className="h-14" />
          <Skeleton className="h-20" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    </div>
  );
}

// --- Thread ---

/**
 * The messages a turn added: a client that resends the whole history still shows each message
 * once (only those after the last assistant message are new).
 */
function newMessages(run: ThreadRunDto): { messages: ChatMessage[]; resent: number } {
  const all = run.input.messages;
  const lastAssistant = all.map((m) => m.role).lastIndexOf('assistant');
  return { messages: all.slice(lastAssistant + 1), resent: lastAssistant + 1 };
}

function TurnTrace({ tenantId, runId }: { tenantId: string; runId: string }) {
  const run = useRunDetail(tenantId, runId);
  const trace = useMemo(() => (run.data ? runSpans(run.data) : null), [run.data]);
  if (run.error) return <ErrorAlert error={run.error} />;
  if (!trace) return <Skeleton className="h-32" />;
  return <TraceLayout spans={trace.spans} totalMs={trace.totalMs} stacked />;
}

function Turn({
  tenantId,
  run,
  index,
  agentName,
  selected,
  matches,
  onSelect,
}: {
  tenantId: string;
  run: ThreadRunDto;
  index: number;
  agentName: string;
  selected: string | null;
  matches: ReturnType<typeof matcher>;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const { messages, resent } = newMessages(run);
  return (
    <li className="grid gap-5" data-testid="thread-turn">
      <div className="text-muted-foreground flex items-center gap-3 text-xs">
        <span className="text-foreground font-medium">Turn {index + 1}</span>
        <span className="bg-border h-px flex-1" aria-hidden />
        {resent > 0 && <span>+{resent} earlier messages resent by the client</span>}
      </div>
      {messages.map((m, i) => (
        <TranscriptItem
          key={i}
          id={i === 0 ? `u-${run.id}` : `u-${run.id}-${i}`}
          role="user"
          tag="User"
          at={run.startedAt}
          selected={i === 0 && selected === `u-${run.id}`}
          dimmed={!matches(m.content)}
          onSelect={() => onSelect(`u-${run.id}`)}
        >
          <MessageBody message={m} />
        </TranscriptItem>
      ))}
      <TranscriptItem
        id={run.id}
        role={run.error ? 'error' : 'agent'}
        tag={agentName}
        at={run.endedAt ?? run.startedAt}
        selected={selected === run.id}
        dimmed={!matches(run.output?.text, run.error?.message)}
        onSelect={() => onSelect(run.id)}
        extra={
          <>
            <TokenTag input={run.inputTokens} output={run.outputTokens} />
            <span className="tabular-nums">{formatDuration(run.latencyMs)}</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-1.5 text-xs"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
              {open ? 'Hide trace' : 'Show trace'}
            </Button>
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" asChild>
              <Link to="/traces/runs/$runId" params={{ runId: run.id }}>
                Open trace
              </Link>
            </Button>
          </>
        }
      >
        {run.output ? (
          <MessageBody message={{ role: 'assistant', content: run.output.text }} />
        ) : run.error ? (
          <p className="px-4 py-3 text-sm">
            <span className="text-destructive font-mono text-xs">{run.error.code}</span> —{' '}
            {run.error.message}
          </p>
        ) : (
          <p className="text-muted-foreground flex items-center gap-2 px-4 py-3 text-sm">
            <span className="bg-tint-blue size-1.5 animate-pulse rounded-full" aria-hidden />
            Running…
          </p>
        )}
      </TranscriptItem>
      {open && (
        <div>
          <TurnTrace tenantId={tenantId} runId={run.id} />
        </div>
      )}
    </li>
  );
}

function ThreadView({ tenantId, search }: { tenantId: string; search: ThreadSearch }) {
  const agents = useAgents(tenantId);
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'session' | 'events'>('session');
  const [query, setQuery] = useState('');
  const params = new URLSearchParams({ agentId: search.agentId, threadId: search.threadId });
  if (search.apiKeyId) params.set('apiKeyId', search.apiKeyId);
  if (search.userId) params.set('userId', search.userId);
  const detail = useQuery({
    queryKey: ['thread', tenantId, params.toString()],
    queryFn: () => api<ThreadDetailDto>(`/threads/detail?${params.toString()}`),
    refetchInterval: (q) => (q.state.data?.runs.some((r) => !isFinal(r.status)) ? 2000 : false),
  });
  if (detail.error) return <ErrorAlert error={detail.error} />;
  if (!detail.data) return <SessionSkeleton />;
  const { thread, runs } = detail.data;
  const agentName = agents.name(thread.agentId);
  const startAt = runs[0] ? ms(runs[0].startedAt) : ms(thread.firstAt);
  const now = Date.now();
  const endAt = Math.max(
    ...runs.map((r) => (r.endedAt ? ms(r.endedAt) : now)),
    ms(thread.lastAt),
    startAt + 1,
  );
  const totalMs = endAt - startAt;
  const running = runs.some((r) => !isFinal(r.status));
  const firstIndex = thread.runCount - runs.length;

  const events: TimelineEvent[] = runs.flatMap((r, i) => {
    const offset = ms(r.startedAt) - startAt;
    const user = newMessages(r).messages.find((m) => m.role === 'user');
    return [
      ...(user
        ? [
            {
              id: `u-${r.id}`,
              kind: 'user' as const,
              label: preview(user.content),
              startMs: offset,
              endMs: offset,
            },
          ]
        : []),
      {
        id: r.id,
        kind: 'agent' as const,
        label: `Turn ${firstIndex + i + 1} · ${r.model}`,
        startMs: offset,
        endMs: r.endedAt ? ms(r.endedAt) - startAt : null,
        failed: isFinal(r.status) && r.status !== 'succeeded',
      },
    ];
  });
  const select = (id: string) => {
    setSelected(id);
    revealEvent(id);
  };
  const matches = matcher(query);
  const matchCount = query.trim()
    ? runs.reduce(
        (n, r) =>
          n +
          newMessages(r).messages.filter((m) => matches(m.content)).length +
          (r.output && matches(r.output.text) ? 1 : 0),
        0,
      )
    : null;
  const activeMs = runs.reduce((n, r) => n + (r.latencyMs ?? 0), 0);
  const failedTurns = runs.filter((r) => isFinal(r.status) && r.status !== 'succeeded').length;
  const last = runs.at(-1);
  const status = running
    ? { label: 'Running', tone: 'running' as const }
    : last && last.status !== 'succeeded'
      ? { label: RUN_STATUS_LABELS[last.status], tone: 'error' as const }
      : { label: 'Idle', tone: 'idle' as const };
  const models = [...new Set(runs.map((r) => r.model))];
  const CallerIcon = thread.caller?.type === 'api_key' ? KeyRound : User;

  return (
    <>
      <PageHeader
        title={`Thread · ${agentName}`}
        breadcrumb={[{ label: 'Traces', to: '/traces' }]}
        actions={
          <Button variant="ghost" size="sm" asChild>
            <Link to="/traces" search={{ tab: 'threads' }}>
              All threads
            </Link>
          </Button>
        }
      />
      <SessionWorkspace
        header={
          <>
            <SessionTitle id={thread.threadId} status={<StatusPill {...status} />} />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
              <SessionChip
                icon={Bot}
                label={agentName}
                title="Agent"
                link={{ to: '/agents/$agentId', params: { agentId: thread.agentId } }}
              />
              <SessionChip icon={Radio} label={RUN_SOURCE_LABELS[thread.source]} title="Source" />
              {thread.caller && (
                <SessionChip
                  icon={CallerIcon}
                  label={
                    thread.caller.name ??
                    (thread.caller.type === 'api_key' ? 'Deleted API key' : 'Deleted user')
                  }
                  title="Caller"
                />
              )}
              {models[0] && (
                <SessionChip
                  icon={Cpu}
                  label={models.at(-1)}
                  title={`Models: ${models.join(', ')}`}
                />
              )}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 sm:ml-3">
                <SessionMetric icon={Timer} label="Active time">
                  {formatDuration(activeMs)}
                </SessionMetric>
                <SessionMetric icon={Layers} label="Tokens in / out">
                  {formatCompact(thread.inputTokens)} / {formatCompact(thread.outputTokens)}
                </SessionMetric>
                <SessionMetric icon={CircleDollarSign} label="Cost">
                  <span
                    title={
                      costCell(thread.costUsd, thread.unpricedRuns).title ?? COST_ESTIMATE_NOTE
                    }
                  >
                    {costCell(thread.costUsd, thread.unpricedRuns).text}
                  </span>
                </SessionMetric>
                <SessionMetric icon={MessagesSquare} label="Turns">
                  {thread.runCount}
                </SessionMetric>
                <SessionMetric icon={Clock} label="Last active">
                  <span title={formatDateTime(thread.lastAt)}>{formatRelative(thread.lastAt)}</span>
                </SessionMetric>
              </div>
            </div>
          </>
        }
        main={
          <>
            <FlowTimeline
              events={events}
              startAt={startAt}
              totalMs={totalMs}
              selected={selected}
              onSelect={select}
              toolbar={<TranscriptSearch value={query} onChange={setQuery} matches={matchCount} />}
            />
            <TranscriptScroll>
              {thread.runCount > runs.length && (
                <p className="text-muted-foreground text-xs">
                  Showing the latest {MAX_THREAD_RUNS} of {thread.runCount} turns.
                </p>
              )}
              <ol className="grid gap-8" aria-label="Turns">
                {runs.map((run, i) => (
                  <Turn
                    key={run.id}
                    tenantId={tenantId}
                    run={run}
                    index={firstIndex + i}
                    agentName={agentName}
                    selected={selected}
                    matches={matches}
                    onSelect={select}
                  />
                ))}
              </ol>
            </TranscriptScroll>
          </>
        }
        panel={
          <PanelTabs
            value={tab}
            onChange={setTab}
            tabs={[
              {
                value: 'session',
                label: 'Session',
                content: (
                  <>
                    <PanelSection title="Thread">
                      <Facts
                        rows={[
                          [
                            'ID',
                            <span
                              className="font-mono text-xs"
                              title={thread.threadId}
                              data-testid="thread-id"
                            >
                              {thread.threadId}
                            </span>,
                          ],
                          ['Status', <StatusPill {...status} />],
                          ['Created', formatDateTime(thread.firstAt)],
                          ['Updated', formatDateTime(thread.lastAt)],
                          [
                            'Agent',
                            <Link
                              to="/agents/$agentId"
                              params={{ agentId: thread.agentId }}
                              className="underline decoration-dotted underline-offset-4"
                            >
                              {agentName}
                            </Link>,
                          ],
                          ['Source', RUN_SOURCE_LABELS[thread.source]],
                          ['Caller', <CallerLabel caller={thread.caller} />],
                          [
                            'Models',
                            <span className="font-mono text-xs" title={models.join(', ')}>
                              {models.join(', ') || '—'}
                            </span>,
                          ],
                        ]}
                      />
                    </PanelSection>
                    <PanelSection
                      title="Tokens"
                      aside={formatNumber(thread.inputTokens + thread.outputTokens)}
                    >
                      <CumulativeChart
                        label="Cumulative tokens over the thread"
                        points={runs.map((r) => ({
                          at: ms(r.endedAt ?? r.startedAt),
                          value: (r.inputTokens ?? 0) + (r.outputTokens ?? 0),
                        }))}
                        startAt={startAt}
                        endAt={endAt}
                      />
                    </PanelSection>
                    <PanelSection title="Usage">
                      <Facts
                        rows={[
                          ['Input tokens', formatNumber(thread.inputTokens)],
                          ['Output tokens', formatNumber(thread.outputTokens)],
                          [
                            'Cost',
                            <span
                              title={
                                costCell(thread.costUsd, thread.unpricedRuns).title ??
                                COST_ESTIMATE_NOTE
                              }
                            >
                              {costCell(thread.costUsd, thread.unpricedRuns).text}
                            </span>,
                          ],
                          ['Turns', formatNumber(thread.runCount)],
                          ['Failed turns', formatNumber(failedTurns)],
                          ['Active time', formatDuration(activeMs)],
                          [
                            'Avg latency',
                            runs.length ? formatDuration(activeMs / runs.length) : '—',
                          ],
                        ]}
                      />
                    </PanelSection>
                  </>
                ),
              },
              {
                value: 'events',
                label: 'Events',
                content: (
                  <ol className="-mx-2 grid gap-0.5" aria-label="Events">
                    {events.map((ev) => (
                      <li key={ev.id}>
                        <button
                          type="button"
                          onClick={() => select(ev.id)}
                          aria-pressed={selected === ev.id}
                          className={cn(
                            'hover:bg-accent focus-visible:ring-ring/50 grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors outline-none focus-visible:ring-3',
                            selected === ev.id && 'bg-accent',
                          )}
                        >
                          <RoleTag role={ev.failed ? 'error' : ev.kind}>
                            {KIND_TAG[ev.kind]}
                          </RoleTag>
                          <span className="truncate">{ev.label}</span>
                          <span className="text-muted-foreground text-xs tabular-nums">
                            {formatTime(startAt + ev.startMs)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ol>
                ),
              },
            ]}
          />
        }
      />
    </>
  );
}

function WorkflowTrace({ tenantId, runId }: { tenantId: string; runId: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const run = useQuery({
    queryKey: ['workflow-run', tenantId, runId],
    queryFn: () => api<WorkflowRunDto>(`/workflow-runs/${runId}`),
    refetchInterval: (q) =>
      q.state.data && !['succeeded', 'failed', 'cancelled'].includes(q.state.data.status)
        ? 2000
        : false,
  });
  const trace = useMemo(() => (run.data ? workflowSpans(run.data) : null), [run.data]);
  if (run.error) return <ErrorAlert error={run.error} />;
  if (!run.data || !trace) return <SessionSkeleton />;
  const r = run.data;
  const [root, ...steps] = trace.spans;
  const events: TimelineEvent[] = [
    {
      id: root?.id ?? 'workflow',
      kind: 'agent',
      label: r.workflowName,
      startMs: 0,
      endMs: root?.endMs ?? null,
      failed: root?.status === 'error',
    },
    ...steps.map((s) => ({
      id: s.id,
      kind: (s.status === 'error'
        ? 'error'
        : s.kind === 'agent'
          ? 'model'
          : 'step') as TimelineKind,
      label: `${s.name} (${s.kind})`,
      startMs: s.startMs,
      endMs: s.endMs,
    })),
  ];
  const done = r.steps.filter((s) => s.status === 'succeeded').length;
  const tone =
    r.status === 'succeeded'
      ? 'idle'
      : r.status === 'failed' || r.status === 'cancelled'
        ? 'error'
        : 'running';
  return (
    <>
      <PageHeader
        title={`Trace · ${r.workflowName}`}
        breadcrumb={[{ label: 'Traces', to: '/traces' }]}
        actions={
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to="/workflows/$workflowId" params={{ workflowId: r.workflowId }}>
                Open workflow
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/traces" search={{ tab: 'workflows' }}>
                All traces
              </Link>
            </Button>
          </>
        }
      />
      <div className="grid gap-5">
        <header className="grid gap-3">
          <SessionTitle
            id={r.id}
            status={<StatusPill label={WORKFLOW_STATUS_LABEL[r.status]} tone={tone} />}
          />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            <SessionChip
              icon={Workflow}
              label={r.workflowName}
              title="Workflow"
              link={{ to: '/workflows/$workflowId', params: { workflowId: r.workflowId } }}
            />
            <SessionChip
              icon={Radio}
              label={`${r.trigger.charAt(0).toUpperCase()}${r.trigger.slice(1)} trigger`}
              title="What started this run"
            />
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 sm:ml-3">
              <SessionMetric icon={Timer} label="Duration">
                {formatDuration(r.latencyMs ?? (r.endedAt ? null : trace.totalMs))}
              </SessionMetric>
              <SessionMetric icon={ListTree} label="Steps succeeded">
                {done}/{r.steps.length} steps
              </SessionMetric>
              <SessionMetric icon={Clock} label="Started">
                <span title={formatDateTime(r.startedAt)}>{formatRelative(r.startedAt)}</span>
              </SessionMetric>
            </div>
          </div>
        </header>
        <EventTimeline
          events={events}
          startAt={ms(r.startedAt)}
          totalMs={trace.totalMs}
          selected={selected}
          onSelect={setSelected}
          labels={{ agent: 'Workflow', model: 'Agent step' }}
        />
        <TraceLayout
          key={runId}
          spans={trace.spans}
          totalMs={trace.totalMs}
          selected={selected ?? trace.spans[0]?.id ?? null}
          onSelect={setSelected}
        />
      </div>
    </>
  );
}

export function RunTracePage() {
  const { runId } = useParams({ from: '/app/traces/runs/$runId' });
  return (
    <RequireTenant>{(tenant) => <RunTrace tenantId={tenant.id} runId={runId} />}</RequireTenant>
  );
}

export function ThreadPage() {
  const search = useSearch({ from: '/app/traces/threads' });
  return (
    <RequireTenant>
      {(tenant) => <ThreadView key={JSON.stringify(search)} tenantId={tenant.id} search={search} />}
    </RequireTenant>
  );
}

export function WorkflowTracePage() {
  const { runId } = useParams({ from: '/app/traces/workflows/$runId' });
  return (
    <RequireTenant>
      {(tenant) => <WorkflowTrace tenantId={tenant.id} runId={runId} />}
    </RequireTenant>
  );
}
